package org.gymly.api.dto

import kotlinx.serialization.Serializable

@Serializable
data class StaffLoginRequestDto(val email: String, val password: String)

@Serializable
data class StaffLoginResponseDto(
    val accessToken: String,
    val tokenType: String,
    val expiresInSeconds: Long,
    val user: StaffLoginUserDto,
)

@Serializable
data class StaffLoginUserDto(
    val id: String,
    val displayName: String,
    val email: String? = null,
    val role: String,
    val preferredLocale: String? = null,
    val membershipOverride: Boolean = false,
)

@Serializable
data class AdminStaffMeDto(
    val id: String,
    val displayName: String,
    val email: String? = null,
    val role: String,
    val roleLabel: String? = null,
    val active: Boolean = true,
    val photoUrl: String? = null,
    val preferredLocale: String? = null,
    val branchId: String? = null,
    val branchName: String? = null,
    val membershipOverride: Boolean = false,
    val sex: String? = null,
    val address: String? = null,
    val phone: String? = null,
    val docId: String? = null,
    val createdAt: String? = null,
    val permissions: List<String> = emptyList(),
    val workSchedule: List<AdminWorkDayScheduleDto> = emptyList(),
)

@Serializable
data class AdminStaffMeUpdateDto(
    val displayName: String,
    val email: String? = null,
    val preferredLocale: String? = null,
    val sex: String,
    val address: String,
    val phone: String? = null,
    val docId: String? = null,
)

@Serializable
data class AdminStaffLocaleDto(
    val preferredLocale: String,
)

@Serializable
data class AdminStaffPasswordDto(
    val currentPassword: String,
    val newPassword: String,
)

@Serializable
data class AdminClientDto(
    val id: String,
    val personId: String,
    val firstName: String,
    val lastName: String,
    val email: String? = null,
    val phone: String? = null,
    val docId: String? = null,
    val birthDate: String? = null,
    val sex: String? = null,
    val address: String? = null,
    val status: String,
    val notes: String? = null,
    val photoUrl: String? = null,
    val portalEnabled: Boolean = false,
    val branchId: String? = null,
    val branchName: String? = null,
    val createdAt: String? = null,
    val accessSyncedAt: String? = null,
    val accessSyncError: String? = null,
    val membershipPlanName: String? = null,
    val accessChannels: List<String> = emptyList(),
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

@Serializable
data class AdminBranchDto(
    val id: String,
    val name: String,
    val address: String? = null,
    val ownerId: String? = null,
    val ownerName: String? = null,
    val hasLogo: Boolean = false,
    val hasBanner: Boolean = false,
    val logoUrl: String? = null,
    val bannerUrl: String? = null,
    val bannerThumbUrl: String? = null,
    val createdAt: String? = null,
)

@Serializable
data class UpsertAdminBranchRequestDto(
    val name: String,
    val address: String? = null,
    val ownerName: String? = null,
    val ownerId: String? = null,
)

@Serializable
data class AdminCompanyRepresentativeDto(
    val id: String? = null,
    val name: String,
    val title: String? = null,
    val docId: String? = null,
    val staffId: String? = null,
    val staffName: String? = null,
    val sortOrder: Int = 0,
)

@Serializable
data class AdminCompanyDto(
    val id: String,
    val name: String,
    val address: String? = null,
    val taxId: String? = null,
    val hasLogo: Boolean = false,
    val hasBanner: Boolean = false,
    val logoUrl: String? = null,
    val bannerUrl: String? = null,
    val bannerThumbUrl: String? = null,
    val representatives: List<AdminCompanyRepresentativeDto> = emptyList(),
    val createdAt: String? = null,
    val brandColor: String? = null,
    /** Zona horaria operativa fija (Venezuela). */
    val timezone: String = "America/Caracas",
    /** Horario de apertura: días con uno o más bloques. */
    val operatingHours: List<AdminWorkDayScheduleDto> = emptyList(),
    val loginShowClients: Boolean = true,
    val loginShowTrainers: Boolean = true,
    val loginShowOnTime: Boolean = true,
    /** Casilla "Monitoreo de Términos y condiciones" (Empresa). */
    val termsMonitoringEnabled: Boolean = false,
    /** La licencia incluye portal o app: solo entonces se ofrece la casilla. */
    val termsMonitoringAvailable: Boolean = false,
    /** Días mínimos entre dos avisos de términos al mismo socio por el mismo canal. */
    val termsNoticeCooldownDays: Int = 3,
)

@Serializable
data class UpsertAdminCompanyRepresentativeDto(
    val name: String,
    val title: String? = null,
    val docId: String? = null,
    val staffId: String? = null,
)

@Serializable
data class UpsertAdminCompanyRequestDto(
    val name: String,
    val address: String? = null,
    val taxId: String? = null,
    val brandColor: String? = null,
    val timezone: String? = null,
    val representatives: List<UpsertAdminCompanyRepresentativeDto> = emptyList(),
    val operatingHours: List<AdminWorkDayScheduleDto> = emptyList(),
    val loginShowClients: Boolean = true,
    val loginShowTrainers: Boolean = true,
    val loginShowOnTime: Boolean = true,
    val termsMonitoringEnabled: Boolean? = null,
    val termsNoticeCooldownDays: Int? = null,
)

@Serializable
data class PublicCompanyDto(
    val id: String,
    val name: String,
    val hasLogo: Boolean = false,
    val logoUrl: String? = null,
    val hasBanner: Boolean = false,
    val bannerUrl: String? = null,
    val brandColor: String? = null,
    val loginShowClients: Boolean = true,
    val loginShowTrainers: Boolean = true,
    val loginShowOnTime: Boolean = true,
)

@Serializable
data class AdminSettingsUserDto(
    val id: String,
    val type: String,
    val displayName: String,
    val email: String? = null,
    val staffRole: String? = null,
    val roleLabel: String? = null,
    val branchId: String? = null,
    val branchName: String? = null,
    val status: String,
    val createdAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsUserRequestDto(
    val type: String,
    val displayName: String,
    val email: String? = null,
    val branchId: String? = null,
    val status: String? = null,
    val password: String? = null,
    val staffRole: String? = null,
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

@Serializable
data class AdminSettingsRoleDto(
    val id: String,
    val name: String,
    val permissions: List<String> = emptyList(),
    val clients: Boolean = false,
    val workers: Boolean = false,
    val payments: Boolean = false,
    val marketing: Boolean = false,
    val store: Boolean = false,
    val settings: Boolean = false,
    val membershipOverride: Boolean = false,
    val createdAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsRoleRequestDto(
    val name: String,
    val permissions: List<String> = emptyList(),
)

@Serializable
data class AdminApprovalRequestDto(
    val id: String,
    val type: String,
    val status: String,
    val summary: String,
    val payloadJson: String? = null,
    val requestedBy: String? = null,
    val requestedByName: String? = null,
    val reviewedBy: String? = null,
    val reviewedByName: String? = null,
    val reviewNote: String? = null,
    val createdAt: String? = null,
    val reviewedAt: String? = null,
)

@Serializable
data class AdminApprovalPendingCountDto(val count: Long = 0)

@Serializable
data class AiReportResponseDto(
    val report: String
)


@Serializable
data class AdminApprovalReviewDto(val note: String? = null)

@Serializable
data class AdminPendingApprovalDto(
    val pendingApproval: Boolean = true,
    val approvalId: String? = null,
    val message: String? = null,
)

@Serializable
data class AdminSettingsMailDto(
    val id: String? = null,
    val host: String = "",
    val port: Int = 587,
    val username: String? = null,
    val fromAddress: String? = null,
    val securityMode: String? = null,
    val useTls: Boolean = true,
    val passwordConfigured: Boolean = false,
    val createdAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsMailRequestDto(
    val host: String,
    val port: Int,
    val username: String? = null,
    val fromAddress: String? = null,
    val password: String? = null,
    val securityMode: String? = null,
    val useTls: Boolean? = null,
)

@Serializable
data class AdminSettingsWhatsAppDto(
    val id: String? = null,
    val enabled: Boolean = false,
    val apiBaseUrl: String = "https://graph.facebook.com",
    val apiVersion: String = "v21.0",
    val phoneNumberId: String? = null,
    val accessTokenConfigured: Boolean = false,
    val templateName: String = "membership_weekly_reminder",
    val templateLanguage: String = "es",
    val templateWelcome: String = "client_welcome",
    val templatePaymentReceived: String = "payment_received",
    val templatePaymentPending: String = "payment_pending",
    val templateMembershipExpiring: String = "membership_expiring",
    val templateMarketing: String? = null,
    val templateTermsNotice: String? = null,
    val templateTermsNoticeApproved: Boolean = false,
    val sendHour: Int = 9,
    val saturdayEnabled: Boolean = true,
    val sundayEnabled: Boolean = true,
    val simulateOnly: Boolean = true,
    val createdAt: String? = null,
    val updatedAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsWhatsAppRequestDto(
    val enabled: Boolean,
    val apiBaseUrl: String? = null,
    val apiVersion: String? = null,
    val phoneNumberId: String? = null,
    val accessToken: String? = null,
    val templateName: String? = null,
    val templateLanguage: String? = null,
    val templateWelcome: String? = null,
    val templatePaymentReceived: String? = null,
    val templatePaymentPending: String? = null,
    val templateMembershipExpiring: String? = null,
    val templateTermsNotice: String? = null,
    val templateTermsNoticeApproved: Boolean? = null,
    val templateMarketing: String? = null,
    val sendHour: Int? = null,
    val saturdayEnabled: Boolean? = null,
    val sundayEnabled: Boolean? = null,
    val simulateOnly: Boolean? = null,
)

@Serializable
data class AdminWhatsAppRunNowResultDto(
    val sentCount: Int = 0,
)

@Serializable
data class AdminSettingsPaymentDto(
    val id: String? = null,
    val paypalEnabled: Boolean = false,
    val paypalClientId: String? = null,
    val paypalSecret: String? = null,
    val paypalMode: String? = null,
    val stripeEnabled: Boolean = false,
    val stripePublicKey: String? = null,
    val stripeSecretKey: String? = null,
    val createdAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsPaymentRequestDto(
    val paypalEnabled: Boolean,
    val paypalClientId: String? = null,
    val paypalSecret: String? = null,
    val paypalMode: String? = null,
    val stripeEnabled: Boolean,
    val stripePublicKey: String? = null,
    val stripeSecretKey: String? = null,
)

@Serializable
data class AdminPaymentMethodTypeDto(
    val id: String,
    val code: String,
    val title: String,
    val description: String? = null,
    val imageUrl: String? = null,
    val enabled: Boolean = true,
    val sortOrder: Int = 0,
    val requiresPhone: Boolean = false,
    val requiresReference: Boolean = false,
    val requiresProof: Boolean = false,
    val autoApprove: Boolean = true,
    val amountLabel: String? = null,
    val provider: String? = null,
    val providerConfig: AdminPaymentMethodProviderConfigDto? = null,
    val systemDefault: Boolean = false,
    val usesForeignCurrency: Boolean = false,
    val foreignCurrencyCode: String? = null,
    val createdAt: String? = null,
)

@Serializable
data class AdminPaymentMethodProviderConfigDto(
    val provider: String,
    val enabled: Boolean = false,
    val publicKeyOrClientId: String? = null,
    val secretMasked: String? = null,
    val paypalMode: String? = null,
    val webhookUrl: String? = null,
)

@Serializable
data class AdminPaymentMethodsBundleDto(
    val methods: List<AdminPaymentMethodTypeDto> = emptyList(),
    val gateways: AdminSettingsPaymentDto? = null,
)

@Serializable
data class UpsertAdminPaymentMethodTypeRequestDto(
    val title: String,
    val description: String? = null,
    val enabled: Boolean,
    val sortOrder: Int? = null,
    val requiresPhone: Boolean,
    val requiresReference: Boolean,
    val requiresProof: Boolean,
    val autoApprove: Boolean? = null,
    val amountLabel: String? = null,
    val provider: String? = null,
    val usesForeignCurrency: Boolean? = null,
    val foreignCurrencyCode: String? = null,
)

@Serializable
data class CreateAdminPaymentMethodTypeRequestDto(
    val code: String,
    val title: String,
    val description: String? = null,
    val enabled: Boolean? = true,
    val sortOrder: Int? = null,
    val requiresPhone: Boolean,
    val requiresReference: Boolean,
    val requiresProof: Boolean,
    val autoApprove: Boolean? = true,
    val amountLabel: String? = null,
    val provider: String? = null,
    val usesForeignCurrency: Boolean? = null,
    val foreignCurrencyCode: String? = null,
)

@Serializable
data class AdminProductLicenseStatusDto(
    val status: String,
    val installId: String,
    val issuedAt: String? = null,
    val expiresAt: String? = null,
    val indefinite: Boolean = false,
    val graceDays: Int = 0,
    val daysRemaining: Int? = null,
    val graceDaysRemaining: Int? = null,
    val features: Set<String> = emptySet(),
    val edition: String? = null,
    val message: String? = null,
    val canActivate: Boolean = false,
)

@Serializable
data class ActivateAdminProductLicenseDto(
    val licenseCode: String,
)

@Serializable
data class AdminSettingsModulesDto(
    val accessEnabled: Boolean = true,
    val exchangeRatesEnabled: Boolean = true,
    val suppressAccessWhileCapturing: Boolean = true,
    val clientAccountsEnabled: Boolean = false,
    val schedulesAutomatic: Boolean = false,
    /** Días de prórroga globales tras vencimiento (acceso + Hikvision). */
    val membershipAccessGraceDays: Int = 0,
    val bulkPaymentsAlwaysPaid: Boolean = false,
    val exchangePanelVisible: Boolean = true,
)

@Serializable
data class UpdateAdminSettingsModulesDto(
    val accessEnabled: Boolean,
    val exchangeRatesEnabled: Boolean,
    val suppressAccessWhileCapturing: Boolean? = null,
    val clientAccountsEnabled: Boolean? = null,
    val schedulesAutomatic: Boolean? = null,
    val membershipAccessGraceDays: Int? = null,
    val bulkPaymentsAlwaysPaid: Boolean? = null,
    val exchangePanelVisible: Boolean? = null,
)

@Serializable
data class AdminSettingsNotificationRuleDto(
    val id: String,
    val eventCode: String,
    val eventLabel: String? = null,
    val emailEnabled: Boolean = false,
    val pushEnabled: Boolean = false,
    val webEnabled: Boolean = false,
    val createdAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsNotificationRuleDto(
    val eventCode: String,
    val emailEnabled: Boolean,
    val pushEnabled: Boolean,
    val webEnabled: Boolean,
)

@Serializable
data class ReplaceAdminSettingsNotificationRulesRequestDto(
    val rules: List<UpsertAdminSettingsNotificationRuleDto>,
)

@Serializable
data class AdminSettingsNotificationScheduleDto(
    val id: String? = null,
    val eventCode: String,
    val eventLabel: String? = null,
    val enabled: Boolean = true,
    val frequency: String = "DAILY",
    val frequencyLabel: String? = null,
    val sendHour: Int = 8,
    val mondayEnabled: Boolean = false,
    val tuesdayEnabled: Boolean = false,
    val wednesdayEnabled: Boolean = false,
    val thursdayEnabled: Boolean = false,
    val fridayEnabled: Boolean = false,
    val saturdayEnabled: Boolean = false,
    val sundayEnabled: Boolean = false,
    val leadDays: String? = "7,3,1",
    val timezone: String = "America/Caracas",
    val inactivateAfterDays: Int = 0,
    val updatedAt: String? = null,
)

@Serializable
data class UpsertAdminSettingsNotificationScheduleDto(
    val eventCode: String,
    val enabled: Boolean,
    val frequency: String,
    val sendHour: Int,
    val mondayEnabled: Boolean = false,
    val tuesdayEnabled: Boolean = false,
    val wednesdayEnabled: Boolean = false,
    val thursdayEnabled: Boolean = false,
    val fridayEnabled: Boolean = false,
    val saturdayEnabled: Boolean = false,
    val sundayEnabled: Boolean = false,
    val leadDays: String? = null,
    val timezone: String? = null,
    val inactivateAfterDays: Int? = null,
)

@Serializable
data class AdminSettingsNotificationTemplateDto(
    val id: String,
    val eventCode: String,
    val eventLabel: String,
    val emailSubject: String,
    val emailBodyText: String,
    val webTitle: String,
    val webBody: String,
)

@Serializable
data class UpsertAdminSettingsNotificationTemplateDto(
    val eventCode: String,
    val emailSubject: String,
    val emailBodyText: String,
    val webTitle: String,
    val webBody: String,
)

@Serializable
data class AdminNotificationTemplateVariablesDto(
    val help: String,
)

@Serializable
data class AdminProductCategoryDto(
    val id: String,
    val name: String,
    val sortOrder: Int = 0,
    val productCount: Int = 0,
)

@Serializable
data class UpsertAdminProductCategoryRequestDto(
    val name: String,
)

@Serializable
data class AdminProductDto(
    val id: String,
    val name: String,
    val category: String,
    val categoryLabel: String? = null,
    val priceCents: Long = 0,
    val priceUsdCents: Long = 0,
    val currency: String = "VES",
    val stockQty: Int = 0,
    val minStockQty: Int = 0,
    val stockLevel: String? = null,
    val stockLevelLabel: String? = null,
    val description: String? = null,
    val active: Boolean = true,
    val branchId: String? = null,
    val branchName: String? = null,
    val createdAt: String? = null,
    val ingredients: String? = null,
    val videoUrl: String? = null,
    val imageUrl: String? = null,
)

@Serializable
data class UpsertAdminProductRequestDto(
    val name: String,
    val category: String,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val currency: String? = "VES",
    val stockQty: Int,
    val minStockQty: Int = 0,
    val description: String? = null,
    val active: Boolean? = true,
    val branchId: String? = null,
    val ingredients: String? = null,
    val videoUrl: String? = null,
)

@Serializable
data class AdminStoreSaleItemDto(
    val id: String? = null,
    val productId: String? = null,
    val productName: String = "",
    val qty: Int = 1,
    val unitPriceCents: Long = 0,
    val lineTotalCents: Long = 0,
)

@Serializable
data class AdminStoreSaleDto(
    val id: String,
    val saleNumber: Long = 0,
    val clientId: String? = null,
    val customerName: String = "",
    val branchId: String? = null,
    val branchName: String? = null,
    val method: String = "CASH",
    val methodLabel: String? = null,
    val origin: String = "STAFF_REGISTERED",
    val originLabel: String? = null,
    val status: String = "PENDING",
    val statusLabel: String? = null,
    val saleDate: String = "",
    val totalCents: Long = 0,
    val amountPaidCents: Long? = null,
    val changeGivenCents: Long = 0,
    val foreignUsdCents: Long? = null,
    val currency: String = "VES",
    val notes: String? = null,
    val payerPhone: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val itemsSummary: String? = null,
    val items: List<AdminStoreSaleItemDto> = emptyList(),
    val createdAt: String? = null,
    val pendingBsCents: Long = 0,
    val pendingUsdCents: Long = 0,
    val payments: List<AdminStorePaymentLineDto> = emptyList(),
    val discountCents: Long = 0,
    val discountReason: String? = null,
    val discountNote: String? = null,
    val discountUsdCents: Long = 0,
)

@Serializable
data class UpsertAdminStoreSaleItemDto(
    val productId: String? = null,
    val productName: String? = null,
    val qty: Int,
    val unitPriceCents: Long? = null,
)

@Serializable
data class UpsertAdminStoreSaleRequestDto(
    val clientId: String? = null,
    val walkInName: String? = null,
    val branchId: String? = null,
    val method: String,
    val origin: String? = null,
    val status: String? = null,
    val saleDate: String? = null,
    val currency: String? = "VES",
    val notes: String? = null,
    val amountPaidCents: Long? = null,
    val changeGivenCents: Long? = null,
    val foreignUsdCents: Long? = null,
    val items: List<UpsertAdminStoreSaleItemDto>,
    val payments: List<AdminStorePaymentLineDto>? = null,
    val pendingBsCents: Long? = null,
    val pendingUsdCents: Long? = null,
    val discount: CollectAdminDiscountDto? = null,
)

@Serializable
data class AdminStorePaymentLineDto(
    val method: String,
    val currency: String = "VES",
    val amountCents: Long = 0,
)

@Serializable
data class ApproveAdminStoreSaleRequestDto(
    val status: String,
)

/** decision: PAID o FAILED (FAILED exige reason). */
@Serializable
data class ProofValidationRequestDto(
    val decision: String,
    val reason: String? = null,
)

@Serializable
data class ProofValidationResponseDto(
    val status: String,
)

@Serializable
data class AdminMembershipOrderPaymentDto(
    val id: String,
    val amountCents: Long = 0,
    val currency: String = "VES",
    val method: String = "",
    val methodLabel: String? = null,
    val status: String = "PAID",
    val statusLabel: String? = null,
    val notes: String? = null,
    val payerPhone: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val paidAt: String? = null,
    val createdAt: String? = null,
    val foreignUsdCents: Long? = null,
    val collectionId: String? = null,
    val changeGivenCents: Long = 0,
)

@Serializable
data class AdminMembershipOrderDto(
    val id: String,
    val orderNumber: Long = 0,
    val clientId: String,
    val clientName: String = "",
    val planId: String,
    val planName: String = "",
    val branchId: String? = null,
    val branchName: String? = null,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val method: String? = null,
    val methodLabel: String? = null,
    val payerPhone: String? = null,
    val origin: String = "STAFF_REGISTERED",
    val originLabel: String? = null,
    val status: String = "PENDING",
    val statusLabel: String? = null,
    val orderDate: String = "",
    val planEndsAt: String? = null,
    val amountCents: Long = 0,
    val paidCents: Long = 0,
    val allocatedCents: Long = 0,
    val planUsdCents: Long = 0,
    val paidUsdCents: Long = 0,
    val pendingUsdCents: Long = 0,
    val pendingBsCents: Long = 0,
    val salesOrderId: String? = null,
    val currency: String = "VES",
    val notes: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val createdAt: String? = null,
    val payments: List<AdminMembershipOrderPaymentDto> = emptyList(),
    val changeGivenCents: Long = 0,
    val discountCents: Long = 0,
    val discountReason: String? = null,
    val discountNote: String? = null,
    val discountUsdCents: Long = 0,
)

@Serializable
data class UpdateAdminMembershipOrderPaymentDateRequestDto(val paidAt: String)

@Serializable
data class AdminMembershipPeriodCoveredDto(
    val covered: Boolean = false,
)

@Serializable
data class UpsertAdminMembershipOrderRequestDto(
    val clientId: String,
    val planId: String,
    val branchId: String? = null,
    val trainerId: String? = null,
    val method: String? = null,
    val payerPhone: String? = null,
    val origin: String? = null,
    val status: String? = null,
    val orderDate: String? = null,
    val amountCents: Long? = null,
    val pendingUsdCents: Long? = null,
    val pendingBsCents: Long? = null,
    val currency: String? = "VES",
    val notes: String? = null,
    val collect: CollectAdminPaymentRequestDto? = null,
    val paymentDates: List<AdminMembershipOrderPaymentDateDto>? = null,
    val endsAt: String? = null,
)

@Serializable
data class AdminMembershipOrderPaymentDateDto(
    val paymentId: String,
    val paidAt: String,
)

/** Cobro de una orden pendiente: solo estado, método y pendientes (lo autoriza Caja y cobros). */
@Serializable
data class RecordAdminMembershipOrderPaymentRequestDto(
    val status: String,
    val method: String? = null,
    val pendingUsdCents: Long,
    val pendingBsCents: Long,
)

@Serializable
data class CreateAdminClientRequestDto(
    val firstName: String,
    val lastName: String,
    val email: String? = null,
    val phone: String? = null,
    val docId: String? = null,
    val birthDate: String? = null,
    val sex: String? = null,
    val address: String? = null,
    val status: String? = null,
    val notes: String? = null,
    val portalPassword: String? = null,
    val branchId: String? = null,
)

@Serializable
data class UpdateAdminClientRequestDto(
    val firstName: String,
    val lastName: String,
    val email: String? = null,
    val phone: String? = null,
    val docId: String? = null,
    val birthDate: String? = null,
    val sex: String? = null,
    val address: String,
    val status: String,
    val notes: String? = null,
    val portalPassword: String? = null,
    val branchId: String? = null,
    val disablePortal: Boolean? = null,
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

@Serializable
data class AdminWorkTimeBlockDto(
    val startTime: String,
    val endTime: String,
)

@Serializable
data class AdminWorkDayScheduleDto(
    val weekday: Int,
    val blocks: List<AdminWorkTimeBlockDto> = emptyList(),
)

@Serializable
data class AdminStaffUserDto(
    val id: String,
    val displayName: String,
    val email: String? = null,
    val role: String,
    val active: Boolean = true,
    val branchId: String? = null,
    val branchName: String? = null,
    val createdAt: String? = null,
    val sex: String? = null,
    val address: String? = null,
    val phone: String? = null,
    val docId: String? = null,
    val photoUrl: String? = null,
    val workSchedule: List<AdminWorkDayScheduleDto> = emptyList(),
    val serviceIds: List<String> = emptyList(),
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

@Serializable
data class CreateAdminWorkerRequestDto(
    val displayName: String,
    val email: String? = null,
    val role: String,
    val password: String? = null,
    val active: Boolean? = true,
    val branchId: String? = null,
    val sex: String,
    val address: String,
    val phone: String? = null,
    val docId: String? = null,
    val workSchedule: List<AdminWorkDayScheduleDto> = emptyList(),
    val serviceIds: List<String> = emptyList(),
)

@Serializable
data class UpdateAdminWorkerRequestDto(
    val displayName: String,
    val email: String? = null,
    val role: String,
    val password: String? = null,
    val active: Boolean = true,
    val branchId: String? = null,
    val sex: String,
    val address: String,
    val phone: String? = null,
    val docId: String? = null,
    val workSchedule: List<AdminWorkDayScheduleDto> = emptyList(),
    val serviceIds: List<String> = emptyList(),
    val inactivationReason: String? = null,
    val inactivationNote: String? = null,
)

@Serializable
data class TerminalReconcileItemDto(
    val employeeNo: String,
    val name: String = "",
    val kind: String = "",
    val reason: String = "",
    val action: String = "",
)

@Serializable
data class TerminalReconcileReportDto(
    val terminalId: String,
    val terminalName: String = "",
    val reviewedAt: String = "",
    val onTerminal: Int = 0,
    val expected: Int = 0,
    val sobran: List<TerminalReconcileItemDto> = emptyList(),
    val faltan: List<TerminalReconcileItemDto> = emptyList(),
)

@Serializable
data class TerminalReconcileActionDto(val employeeNo: String, val action: String)

@Serializable
data class TerminalReconcileApplyRequestDto(val items: List<TerminalReconcileActionDto>)

@Serializable
data class TerminalReconcileResultDto(val applied: Int = 0, val errors: List<String> = emptyList())

@Serializable
data class AdminServiceDto(
    val id: String,
    val name: String,
    val type: String,
    val durationMin: Int? = null,
    val priceCents: Long = 0,
    val active: Boolean = true,
    val createdAt: String? = null,
    val description: String? = null,
    val imageUrl: String? = null,
)

@Serializable
data class CreateAdminServiceRequestDto(
    val name: String,
    val type: String,
    val durationMin: Int? = null,
    val active: Boolean = true,
    val description: String? = null,
)

@Serializable
data class UpdateAdminServiceRequestDto(
    val name: String,
    val type: String,
    val durationMin: Int? = null,
    val active: Boolean = true,
    val description: String? = null,
)

@Serializable
data class AdminPlanServiceSummaryDto(
    val id: String,
    val name: String,
    val type: String,
)

@Serializable
data class AdminMembershipPlanDto(
    val id: String,
    val name: String,
    val billingPeriod: String,
    val scheduleType: String = "MIXED",
    val priceCents: Long = 0,
    val priceUsdCents: Long = 0,
    val active: Boolean = true,
    val services: List<AdminPlanServiceSummaryDto> = emptyList(),
    val createdAt: String? = null,
    val reminderLeadDays: String? = null,
    val effectiveReminderLeadDays: String? = null,
)

@Serializable
data class CreateAdminMembershipPlanRequestDto(
    val name: String,
    val billingPeriod: String,
    val scheduleType: String = "MIXED",
    val priceCents: Long = 0,
    val priceUsdCents: Long = 0,
    val active: Boolean = true,
    val serviceIds: List<String> = emptyList(),
    val reminderLeadDays: String? = null,
)

@Serializable
data class UpdateAdminMembershipPlanRequestDto(
    val name: String,
    val billingPeriod: String,
    val scheduleType: String = "MIXED",
    val priceCents: Long = 0,
    val priceUsdCents: Long = 0,
    val active: Boolean = true,
    val serviceIds: List<String> = emptyList(),
    val reminderLeadDays: String? = null,
)

@Serializable
data class AdminMembershipDto(
    val id: String,
    val clientId: String,
    val clientName: String? = null,
    val planId: String,
    val planName: String,
    val status: String,
    val startsAt: String,
    val endsAt: String? = null,
    val branchId: String? = null,
    val branchName: String? = null,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val accessMode: String? = null,
    val manualReason: String? = null,
    val manualByStaffName: String? = null,
    val createdAt: String? = null,
)

@Serializable
data class AdminMembershipAccessHistoryDto(
    val id: String,
    val eventType: String,
    val eventLabel: String,
    val planName: String,
    val trainerName: String? = null,
    val status: String,
    val startsAt: String,
    val endsAt: String? = null,
    val accessMode: String? = null,
    val reason: String? = null,
    val staffName: String? = null,
    val orderNumber: Long? = null,
    val amountCents: Long? = null,
    val occurredAt: String,
)

@Serializable
data class AdminStaffAuditEventDto(
    val id: String,
    val staffId: String,
    val staffName: String,
    val staffEmail: String? = null,
    val eventType: String,
    val eventLabel: String,
    val moduleCode: String? = null,
    val moduleLabel: String? = null,
    val detail: String? = null,
    val sessionId: String? = null,
    val durationSeconds: Long? = null,
    val entityType: String? = null,
    val entityId: String? = null,
    val occurredAt: String,
)

@Serializable
data class AdminStaffAuditPurgeResponseDto(
    val removed: Int,
    val retentionDays: Int,
    val message: String,
)

@Serializable
data class RecordAdminStaffAuditRequestDto(
    val eventType: String,
    val moduleCode: String? = null,
    val moduleLabel: String? = null,
    val detail: String? = null,
    val sessionId: String? = null,
    val durationSeconds: Long? = null,
)

@Serializable
data class CreateAdminMembershipRequestDto(
    val clientId: String,
    val planId: String,
    val status: String? = "ACTIVE",
    val startsAt: String,
    val endsAt: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
    val manualReason: String? = null,
)

@Serializable
data class UpdateAdminMembershipRequestDto(
    val clientId: String,
    val planId: String,
    val status: String,
    val startsAt: String,
    val endsAt: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
    val manualReason: String? = null,
)

@Serializable
data class ManualMembershipRequestDto(
    val reason: String? = null,
)

@Serializable
data class CreateAdminTrainingSessionRequestDto(
    val sessionDate: String,
    val attendance: String,
    val serviceId: String? = null,
)

@Serializable
data class AdminTrainingSessionDto(
    val id: String,
    val membershipId: String,
    val sessionDate: String,
    val attendance: String,
    val serviceId: String? = null,
    val serviceName: String? = null,
)

@Serializable
data class AdminOrderItemDto(
    val id: String,
    val serviceId: String? = null,
    val serviceName: String? = null,
    val description: String? = null,
    val qty: Int,
    val unitPriceCents: Long,
)

@Serializable
data class AdminOrderDto(
    val id: String,
    val orderNumber: Long? = null,
    val clientId: String,
    val clientName: String? = null,
    val status: String,
    val totalAmountCents: Long,
    val createdAt: String? = null,
    val lines: List<AdminOrderItemDto> = emptyList(),
)

@Serializable
data class CreateAdminOrderRequestDto(
    val clientId: String,
    val status: String? = "OPEN",
    val lines: List<CreateAdminOrderLineDto>,
)

@Serializable
data class UpdateAdminOrderRequestDto(
    val clientId: String? = null,
    val status: String,
    val lines: List<CreateAdminOrderLineDto>? = null,
)

@Serializable
data class CreateAdminOrderLineDto(
    val serviceId: String? = null,
    val qty: Int = 1,
    val unitPriceCents: Long,
    val description: String? = null,
)

@Serializable
data class AdminPaymentDto(
    val id: String,
    val orderId: String,
    val clientId: String,
    val clientName: String? = null,
    val amountCents: Long,
    val currency: String,
    val method: String,
    val status: String,
    val origin: String? = null,
    val notes: String? = null,
    val payerPhone: String? = null,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
    val paidAt: String? = null,
    val createdAt: String? = null,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val exchangeEffectiveDate: String? = null,
)

@Serializable
data class CreateAdminPaymentRequestDto(
    val orderId: String? = null,
    val clientId: String? = null,
    val amountCents: Long,
    val currency: String? = "VES",
    val method: String,
    val status: String? = "PAID",
    val notes: String? = null,
    val payerPhone: String? = null,
    val paidAt: String? = null,
    val planId: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
)

@Serializable
data class UpdateAdminPaymentRequestDto(
    val amountCents: Long,
    val currency: String? = "VES",
    val method: String,
    val status: String,
    val notes: String? = null,
    val payerPhone: String? = null,
    val paidAt: String? = null,
)

@Serializable
data class CollectAdminPaymentLineDto(
    val method: String,
    val amountCents: Long,
    val foreignUsdCents: Long? = null,
    val status: String? = null,
    val payerPhone: String? = null,
    val notes: String? = null,
    val proofPath: String? = null,
)

@Serializable
data class CollectAdminPaymentTargetDto(
    val orderId: String? = null,
    val clientId: String? = null,
    val membershipOrderId: String? = null,
    val amountCents: Long,
    val planId: String? = null,
    val branchId: String? = null,
    val trainerId: String? = null,
    /** Descuento de esta persona dentro del cobro (alta masiva). Null = sin descuento. */
    val discount: CollectAdminDiscountDto? = null,
)

@Serializable
data class CollectAdminPaymentRequestDto(
    val currency: String? = "VES",
    val targets: List<CollectAdminPaymentTargetDto>,
    val lines: List<CollectAdminPaymentLineDto>,
    val paidAt: String? = null,
    val changeGivenCents: Long? = null,
    val changeGivenUsdCents: Long? = null,
    val discount: CollectAdminDiscountDto? = null,
)

@Serializable
data class CollectAdminDiscountDto(
    val cents: Long,
    val reason: String,
    val note: String? = null,
    val usdCents: Long? = null,
)

@Serializable
data class CollectAdminPaymentResponseDto(
    val collectionId: String? = null,
    val payments: List<AdminPaymentDto> = emptyList(),
    val targets: List<CollectAdminPaymentTargetResultDto> = emptyList(),
)

@Serializable
data class CollectAdminPaymentTargetResultDto(
    val clientId: String,
    val salesOrderId: String,
    val membershipOrderId: String? = null,
    val amountCents: Long,
)

@Serializable
data class AdminScheduleDto(
    val id: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val capacity: Int = 12,
    val kind: String? = null,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val serviceId: String? = null,
    val serviceName: String? = null,
    val serviceType: String? = null,
    val shift: String? = null,
    val shiftLabel: String? = null,
    val weekStart: String? = null,
    val createdAt: String? = null,
    val trainerAbsent: Boolean = false,
)

@Serializable
data class SetTrainerAbsentRequestDto(
    val absent: Boolean,
)

@Serializable
data class CreateAdminScheduleRequestDto(
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val capacity: Int,
    val kind: String? = null,
    val trainerId: String? = null,
    val serviceId: String? = null,
    val weekStart: String? = null,
)

@Serializable
data class UpdateAdminScheduleRequestDto(
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val title: String,
    val capacity: Int,
    val kind: String? = null,
    val trainerId: String? = null,
    val serviceId: String? = null,
    val weekStart: String? = null,
)

@Serializable
data class DuplicateScheduleWeekRequestDto(
    val sourceWeekStart: String,
    val targetWeekStart: String,
    val replaceExisting: Boolean = false,
)

@Serializable
data class DuplicateScheduleWeekResponseDto(
    val copied: Int,
)

@Serializable
data class AdminVisitDto(
    val id: String,
    val clientId: String? = null,
    val staffId: String? = null,
    val clientName: String? = null,
    val staffName: String? = null,
    val visitDate: String,
    val occurredAt: String,
    val source: String,
    val scheduleId: String? = null,
    val scheduleTitle: String? = null,
    val trainerId: String? = null,
    val trainerName: String? = null,
    val bookingId: String? = null,
    val notes: String? = null,
    val createdAt: String? = null,
)

@Serializable
data class CreateAdminVisitRequestDto(
    val clientId: String? = null,
    val staffId: String? = null,
    val scheduleId: String? = null,
    val trainerId: String? = null,
    val occurredAt: String? = null,
    val notes: String? = null,
)

@Serializable
data class DashboardSummaryDto(
    val activeClients: Long,
    val trainers: Long,
    val onTimePaymentPercent: Int,
    val scheduleSlots: Long,
    val bookingsToday: Long,
    val bookingsThisWeek: Long,
)

/** Referencia de pago registrada en un día (GET /api/payments/references): clave de 5 dígitos y dónde está. */
@Serializable
data class AdminPaymentReferenceDto(
    val key: String,
    val label: String,
    val paymentId: String? = null,
    val storeSaleId: String? = null,
)

/** Stats del panel de login (GET /api/public/stats, sin auth). */
@Serializable
data class PublicStatsDto(
    val activeClients: Long,
    val trainers: Long,
    val onTimePaymentPercent: Int,
)

@Serializable
data class AdminBookingDto(
    val id: String,
    val clientId: String,
    val clientName: String,
    val scheduleId: String,
    val title: String,
    val sessionDate: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val status: String,
    val notes: String? = null,
    val createdAt: String? = null,
)

@Serializable
data class CreateAdminBookingRequestDto(
    val clientId: String,
    val scheduleId: String,
    val sessionDate: String? = null,
    val notes: String? = null,
)

@Serializable
data class UpdateAdminBookingRequestDto(
    val clientId: String,
    val scheduleId: String,
    val sessionDate: String? = null,
    val notes: String? = null,
)

@Serializable
data class AdminPaymentMethodTotalDto(
    val method: String,
    val methodLabel: String,
    val totalCents: Long,
    val count: Int,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
)

@Serializable
data class AdminSessionReportRowDto(
    val date: String,
    val occurredAt: String = "",
    val subjectType: String = "CLIENT",
    val subjectTypeLabel: String = "Cliente",
    val subjectId: String? = null,
    val subjectName: String = "—",
    val source: String = "MANUAL",
    val sourceLabel: String = "Manual",
    val scheduleTitle: String = "—",
    val trainerName: String = "",
)

@Serializable
data class AdminSessionReportDto(
    val sessions: Int,
    val clients: Int = 0,
    val staff: Int = 0,
    val accessVisits: Int = 0,
    val currency: String = "VES",
    val rows: List<AdminSessionReportRowDto> = emptyList(),
)

@Serializable
data class AdminMembershipReportRowDto(
    val membershipId: String,
    val startsAt: String,
    val endsAt: String? = null,
    val branchId: String? = null,
    val branchName: String,
    val trainerId: String? = null,
    val trainerName: String,
    val clientName: String,
    val planName: String,
    val status: String,
    val statusLabel: String = status,
    val method: String = "",
    val methodLabel: String = "—",
    val priceCents: Long,
    val currency: String = "VES",
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val paidCents: Long = 0,
    val discountCents: Long = 0,
    val pendingBsCents: Long = 0,
    val pendingUsdCents: Long = 0,
    val discountUsdCents: Long = 0,
    val changeGivenCents: Long = 0,
)

@Serializable
data class AdminMembershipReportDto(
    val memberships: Int,
    val active: Int,
    val incomeCents: Long,
    val trainers: Int,
    val currency: String = "VES",
    val rows: List<AdminMembershipReportRowDto> = emptyList(),
    val totalsByMethod: List<AdminPaymentMethodTotalDto> = emptyList(),
    val discountCents: Long = 0,
    val pendingBsCents: Long = 0,
    val pendingUsdCents: Long = 0,
)

@Serializable
data class AdminSalesReportRowDto(
    val saleDate: String,
    val saleNumber: Long,
    val clientId: String? = null,
    val customerName: String,
    val branchId: String? = null,
    val branchName: String,
    val trainerId: String? = null,
    val trainerName: String,
    val method: String,
    val methodLabel: String,
    val origin: String,
    val originLabel: String,
    val status: String,
    val statusLabel: String,
    val itemsSummary: String,
    val totalCents: Long,
    val amountPaidCents: Long = 0,
    val changeGivenCents: Long = 0,
    val currency: String = "VES",
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val productName: String = "",
    val productId: String? = null,
    val qty: Int = 0,
    val stockQty: Int = 0,
    val pendingBsCents: Long = 0,
    val pendingUsdCents: Long = 0,
    val paymentsSummary: String = "",
    val discountCents: Long = 0,
    val discountUsdCents: Long = 0,
)

@Serializable
data class AdminSalesReportDto(
    val sales: Int,
    val paid: Int,
    val pending: Int,
    val incomeCents: Long,
    val changeGivenCents: Long = 0,
    val currency: String = "VES",
    val rows: List<AdminSalesReportRowDto> = emptyList(),
    val totalsByMethod: List<AdminPaymentMethodTotalDto> = emptyList(),
    val pendingBsCents: Long = 0,
    val pendingUsdCents: Long = 0,
    val discountCents: Long = 0,
)

@Serializable
data class AdminEnrollmentReportRowDto(
    val enrolledAt: String,
    val clientId: String? = null,
    val clientName: String,
    val planId: String? = null,
    val planName: String,
    val trainerId: String? = null,
    val trainerName: String,
    val status: String,
    val statusLabel: String = status,
)

@Serializable
data class AdminInventoryReportRowDto(
    val productId: String? = null,
    val name: String,
    val categoryId: String? = null,
    val categoryName: String,
    val stockQty: Int,
    val minStockQty: Int,
    val stockLevel: String,
    val stockLevelLabel: String,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val currency: String = "VES",
    val description: String = "—",
    val active: Boolean = true,
    val activeLabel: String = "Activo",
)

@Serializable
data class AdminInventoryReportDto(
    val products: Int,
    val units: Int,
    val low: Int,
    val out: Int,
    val currency: String = "VES",
    val rows: List<AdminInventoryReportRowDto> = emptyList(),
)

@Serializable
data class AdminEnrollmentReportDto(
    val enrollments: Int,
    val clients: Int,
    val trainers: Int,
    val plans: Int,
    val rows: List<AdminEnrollmentReportRowDto> = emptyList(),
)

@Serializable
data class AdminCashMethodAmountDto(
    val method: String = "",
    val methodLabel: String = "",
    val amountCents: Long = 0,
    val foreignUsdCents: Long? = null,
    val reference: String? = null,
)

@Serializable
data class AdminCashReconciliationRowDto(
    val timeLabel: String,
    val sourceType: String,
    val sourceLabel: String,
    val clientName: String,
    val method: String,
    val methodLabel: String,
    val amountCents: Long,
    val currency: String,
    val detail: String,
    val foreignUsdCents: Long? = null,
    val foreignEurCents: Long? = null,
    val methodAmounts: List<AdminCashMethodAmountDto> = emptyList(),
    val changeGivenCents: Long = 0,
    /** Día de pago (dd/MM); acompañar con registeredLabel si digitaron otro día. */
    val paidDateLabel: String? = null,
    /** dd/MM HH:mm de digitación; null si coincide el día con el pago. */
    val registeredLabel: String? = null,
)

@Serializable
data class AdminPortalActivationDto(
    val sent: Boolean,
    val temporaryPassword: String? = null,
    val message: String = "",
)

@Serializable
data class AdminCashReconciliationReportDto(
    val date: String,
    val currency: String,
    val totalInCents: Long,
    val totalOutCents: Long,
    val netCents: Long,
    val rows: List<AdminCashReconciliationRowDto> = emptyList(),
    val totalsByMethod: List<AdminPaymentMethodTotalDto> = emptyList(),
)

@Serializable
data class AdminSearchHitDto(
    val id: String,
    val type: String,
    val title: String,
    val subtitle: String? = null,
)

@Serializable
data class AdminNotificationItemDto(
    val id: String,
    val title: String,
    val body: String? = null,
    val read: Boolean = false,
    val createdAt: String? = null,
    val kind: String? = null,
    val refType: String? = null,
    val refId: String? = null,
)

@Serializable
data class AdminNotificationListDto(
    val unreadCount: Long = 0,
    val items: List<AdminNotificationItemDto> = emptyList(),
)

@Serializable
data class AdminAccessTerminalDto(
    val id: String,
    val name: String,
    val enabled: Boolean = false,
    val host: String,
    val port: Int = 80,
    val username: String,
    val password: String? = null,
    val branchId: String? = null,
    val branchName: String? = null,
    val webhookSecret: String? = null,
    val attendanceMode: String? = null,
    val webhookNotifyHost: String? = null,
    val webhookNotifyPort: Int = 18080,
    val lastSeenAt: String? = null,
    val lastError: String? = null,
    val createdAt: String? = null,
    val lastReviewAt: String? = null,
    val pendingReview: Int = 0,
)

@Serializable
data class UpsertAdminAccessTerminalRequestDto(
    val name: String,
    val enabled: Boolean,
    val host: String,
    val port: Int,
    val username: String,
    val password: String? = null,
    val branchId: String? = null,
    val webhookSecret: String? = null,
    val attendanceMode: String,
    val webhookNotifyHost: String? = null,
    val webhookNotifyPort: Int = 18080,
)

@Serializable
data class AdminAccessEventDto(
    val id: String,
    val terminalId: String,
    val terminalName: String? = null,
    val clientId: String? = null,
    val clientName: String? = null,
    val staffId: String? = null,
    val bookingId: String? = null,
    val employeeNo: String? = null,
    val eventTime: String? = null,
    val authType: String? = null,
    val success: Boolean = true,
    val result: String,
    val resultLabel: String? = null,
)

@Serializable
data class AdminAccessTestDto(
    val ok: Boolean = false,
    val message: String? = null,
)

@Serializable
data class AdminAccessSimulateRequestDto(
    val clientId: String? = null,
    val staffId: String? = null,
)

@Serializable
data class AdminStaffAccessSyncDto(
    val staffId: String,
    val accessSyncedAt: String? = null,
    val accessSyncError: String? = null,
)

@Serializable
data class AdminAccessSyncDto(
    val clientId: String,
    val accessSyncedAt: String? = null,
    val accessSyncError: String? = null,
)

@Serializable
data class AdminCaptureFaceDto(
    val bytesBase64: String,
    val contentType: String,
    val fileName: String,
)

@Serializable
data class AdminExchangeRateQuoteDto(
    val effectiveDate: String? = null,
    val eurRate: Double? = null,
    val usdRate: Double? = null,
    val cnyRate: Double? = null,
    val tryRate: Double? = null,
    val rubRate: Double? = null,
    val source: String? = null,
    val status: String,
    val fetchedAt: String? = null,
    val manualOverrideEnabled: Boolean = false,
    val divisasUsesEurRate: Boolean = true,
)

@Serializable
data class AdminExchangeRateHistoryDto(
    val effectiveDate: String,
    val eurRate: Double,
    val usdRate: Double,
    val source: String,
    val status: String,
    val fetchedAt: String? = null,
)

@Serializable
data class AdminExchangeRateSettingsDto(
    val manualOverrideEnabled: Boolean,
    val manualEurRate: Double? = null,
    val manualUsdRate: Double? = null,
    val manualCnyRate: Double? = null,
    val manualTryRate: Double? = null,
    val manualRubRate: Double? = null,
    val manualEffectiveDate: String? = null,
    val divisasUsesEurRate: Boolean = true,
    val current: AdminExchangeRateQuoteDto,
    val history: List<AdminExchangeRateHistoryDto> = emptyList(),
)

@Serializable
data class ManualExchangeRateRequestDto(
    val effectiveDate: String,
    val eurRate: Double,
    val usdRate: Double,
)

@Serializable
data class UpdateAdminExchangeRateSettingsDto(
    val manualOverrideEnabled: Boolean,
    val manualEurRate: Double? = null,
    val manualUsdRate: Double? = null,
    val manualCnyRate: Double? = null,
    val manualTryRate: Double? = null,
    val manualRubRate: Double? = null,
    val manualEffectiveDate: String? = null,
    val divisasUsesEurRate: Boolean = true,
)

@Serializable
data class ConvertDivisasRequestDto(
    val usdCents: Long,
)

@Serializable
data class AdminDivisasConversionDto(
    val usdCents: Long,
    val eurCents: Long,
    val vesCents: Long,
    val eurRate: Double,
    val usdRate: Double,
    val effectiveDate: String? = null,
)

@Serializable
data class AdminClientAccountSummaryDto(
    val clientId: String,
    val clientName: String,
    val clientEmail: String? = null,
    val balanceCents: Long,
    val pendingDebtCents: Long,
    val currency: String,
)

@Serializable
data class AdminClientAccountEntryDto(
    val id: String,
    val entryType: String,
    val amountCents: Long,
    val balanceAfterCents: Long,
    val description: String? = null,
    val membershipOrderId: String? = null,
    val membershipOrderNumber: Long? = null,
    val salesOrderId: String? = null,
    val salesOrderNumber: Long? = null,
    val paymentId: String? = null,
    val effectiveAt: String,
)

@Serializable
data class AdminClientAccountStatementDto(
    val clientId: String,
    val clientName: String,
    val balanceCents: Long,
    val pendingDebtCents: Long,
    val currency: String,
    val entries: List<AdminClientAccountEntryDto> = emptyList(),
)

@Serializable
data class AdminClientAccountApplyCreditRequestDto(
    val membershipOrderId: String? = null,
    val salesOrderId: String? = null,
    val amountCents: Long? = null,
)

@Serializable
data class AdminClientAccountApplyCreditResultDto(
    val clientId: String,
    val appliedCents: Long,
    val balanceAfterCents: Long,
    val salesOrderId: String? = null,
    val membershipOrderId: String? = null,
)

@Serializable
data class AdminClientWalletDepositItemDto(
    val clientId: String,
    val shareCents: Long,
)

@Serializable
data class AdminClientWalletDepositBatchRequestDto(
    val deposits: List<AdminClientWalletDepositItemDto>,
    val paymentTotalCents: Long,
    val description: String? = null,
)

@Serializable
data class AdminClientWalletDepositBatchResultDto(
    val clientCount: Int,
    val paymentTotalCents: Long,
)

@Serializable
data class AdminAiProviderSetupRequestDto(
    val name: String,
    val modelName: String,
    val apiKey: String,
    val priority: Int = 1,
)

@Serializable
data class PasswordSuggestionDto(val password: String)
